import type { Command } from 'commander';
import { randomUUID } from 'node:crypto';
import { unwrapDataResponse } from '@wport/core';
import { enterprisePost } from '../../../lib/enterprise-client';
import { resolveApiKey } from '../../../lib/credentials-store';
import { resolveContext } from '../../../lib/global-opts';
import { CliError, ExitCode } from '../../../lib/errors';
import { readTextInput } from '../../../lib/io-helpers';
import { printJson, type OutputFormat } from '../../../lib/output';

interface RespondFlags {
	subject?: string;
	body?: string;
	bodyFile?: string;
	encJobId?: string;
	idempotencyKey?: string;
}

/** respond 回應：後端回 { enc_resume_id, sent_at }（不含信件內容 / 求職者聯絡方式）。 */
interface RespondResult {
	enc_resume_id?: string;
	sent_at?: string;
	[k: string]: unknown;
}

/**
 * 信件內文來源：--body / --body-file 擇一必填、互斥（both 或 neither → exit 2、不發請求）。
 * --body-file 讀檔 / stdin 純文字（readTextInput，內含空輸入檢查）。
 */
export function resolveRespondBody(
	flags: { body?: string; bodyFile?: string },
	options: { timeoutMs?: number } = {}
): string {
	const hasBody = flags.body !== undefined;
	const hasBodyFile = flags.bodyFile !== undefined;
	if (hasBody === hasBodyFile) {
		throw new CliError('Provide exactly one of --body or --body-file', ExitCode.InvalidArgument);
	}
	if (hasBody) {
		if (!flags.body!.trim()) throw new CliError('--body must not be empty', ExitCode.InvalidArgument);
		return flags.body!;
	}
	return readTextInput(flags.bodyFile!, { timeoutMs: options.timeoutMs });
}

export async function runEnterpriseTalentsRespond(
	ctx: { baseUrl: string; locale: string; timeoutMs: number; format: OutputFormat },
	apiKey: string,
	encResumeId: string,
	flags: RespondFlags,
	idempotencyKey: string
): Promise<void> {
	const trimmedId = encResumeId.trim();
	if (!trimmedId) {
		throw new CliError('enc_resume_id must not be empty', ExitCode.InvalidArgument);
	}
	const subject = (flags.subject ?? '').trim();
	if (!subject) {
		throw new CliError('--subject is required and must not be empty', ExitCode.InvalidArgument);
	}
	const body = resolveRespondBody(flags, { timeoutMs: ctx.timeoutMs });

	// enc_job_id 為 optional：帶則後端解密後精準選該職缺的聊天室，不帶則回覆最近一筆應徵（向後相容）。
	// CLI 端不解密、直接帶加密 id；空白視同未帶。
	const encJobId = flags.encJobId?.trim();
	const payload: Record<string, unknown> = { subject, body };
	if (encJobId) payload.enc_job_id = encJobId;

	const { body: respBody } = await enterprisePost(
		{ baseUrl: ctx.baseUrl, locale: ctx.locale, timeoutMs: ctx.timeoutMs, apiKey },
		`/talents/${encodeURIComponent(trimmedId)}/respond`,
		payload,
		{ idempotencyKey }
	);
	const result = unwrapDataResponse<RespondResult>(respBody);
	if (ctx.format === 'json') {
		printJson(result);
		return;
	}
	process.stdout.write(`Replied to ${result.enc_resume_id ?? trimmedId}; sent at ${result.sent_at ?? '(unknown)'}.\n`);
}

export function registerEnterpriseTalentsRespond(parent: Command): void {
	parent
		.command('respond <enc_resume_id>')
		.description('Reply to an applicant (in-app message; inherits the company daily reply quota)')
		.requiredOption('--subject <subject>', 'message subject (max 200)')
		.option('--body <text>', 'message body text (max 5000)')
		.option('--body-file <path>', 'read message body from a file, or "-" for stdin')
		.option('--enc-job-id <enc_id>', 'reply to a specific job posting; omit to reply to the most recent application')
		.option('--idempotency-key <key>', 'reuse across retries to avoid duplicate sends (default: a fresh UUID)')
		.action(async (encResumeId: string, flags: RespondFlags, command: Command) => {
			const ctx = resolveContext(command);
			const globals = command.optsWithGlobals() as { apiKey?: string };
			const { key } = resolveApiKey(globals.apiKey);
			await runEnterpriseTalentsRespond(ctx, key, encResumeId, flags, flags.idempotencyKey ?? randomUUID());
		});
}

// Internal helpers exposed for tests. Not part of the public CLI API.
export const __test__ = { resolveRespondBody };
